import { useState, useCallback } from "react";
import { TestDocumentTypeResponse } from "../types/documentTypes";
import { useAuthFetch } from "../../../utils/useAuthFetch";

export const useTestDocumentType = (workspace_id: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResults, setTestResults] =
    useState<TestDocumentTypeResponse | null>(null);
  const authFetch = useAuthFetch();

  const testDocumentType = useCallback(
    async (documentTypeId: number) => {
      // Prevent multiple simultaneous requests
      if (loading) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const url = `/api/workspace/meridian/admin/document_types/${documentTypeId}/test?workspace_id=${workspace_id}`;

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, 30000); // 30 second timeout

        let response;
        try {
          response = await authFetch(url, "test document type", {
            method: "POST",
            signal: controller.signal,
          });

          clearTimeout(timeoutId);
        } catch (err) {
          clearTimeout(timeoutId);
          throw err;
        }

        // Check if response has the expected structure
        if (response && typeof response === "object") {
          setTestResults(response);
        } else {
          console.error("Invalid response format:", response);
          setError("Invalid response format from server");
        }
      } catch (err: any) {
        // Handle AbortError specifically
        if (err.name === "AbortError") {
          console.error("Request was aborted (timeout or cancelled):", err);
          setError("Request timed out. Please try again.");
        } else {
          console.error("Error testing document type:", err);
          setError(err.message || "Failed to test document type");
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch and loading accessed in callback
    [workspace_id],
  );

  const resetTestResults = useCallback(() => {
    setTestResults(null);
    setError(null);
  }, []);

  return {
    testDocumentType,
    loading,
    error,
    testResults,
    resetTestResults,
  };
};
